// ** Next Imports
import { Link, useLocation, useNavigate } from "react-router-dom";

// ** MUI Imports
import Chip from "@mui/material/Chip";
import ListItem from "@mui/material/ListItem";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemButton from "@mui/material/ListItemButton";

// ** Configs Import
import themeConfig from "../../../../../configs/themeConfig";

// ** Custom Components Imports
import UserIcon from "../../../../../layouts/components/UserIcon";
import Translations from "../../../../../layouts/components/Translations";
import CustomAvatar from "../../../../../@core/components/mui/avatar";
import CanViewNavLink from "../../../../../layouts/components/acl/CanViewNavLink";

// ** Util Imports

import { hexToRGBA } from "../../../../utils/hex-to-rgba";
import { handleURLQueries } from "../../../../layouts/utils";
import { Badge } from "@mui/material";

// ** Styled Components
const MenuNavLink = styled(ListItemButton)(({ theme }) => ({
  width: "100%",
  marginLeft: theme.spacing(3.5),
  marginRight: theme.spacing(3.5),
  borderRadius: theme.shape.borderRadius,
  transition: "padding-left .25s ease-in-out, padding-right .25s ease-in-out",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  "&.active": {
    "&, &:hover": {
      boxShadow: `0px 2px 6px ${hexToRGBA(theme.palette.primary.main, 0.48)}`,
      background: `linear-gradient(72.47deg, ${theme.direction === "ltr"
        ? theme.palette.primary.main
        : hexToRGBA(theme.palette.primary.main, 0.7)
        } 22.16%, ${theme.direction === "ltr"
          ? hexToRGBA(theme.palette.primary.main, 0.7)
          : theme.palette.primary.main
        } 76.47%)`,
      "&.Mui-focusVisible": {
        background: `linear-gradient(72.47deg, ${theme.palette.primary.dark
          } 22.16%, ${hexToRGBA(theme.palette.primary.dark, 0.7)} 76.47%)`,
      },
    },
    "& .MuiTypography-root, & svg": {
      color: `${theme.palette.common.white} !important`,
    },
  },
}));

const MenuItemTextMetaWrapper = styled(Box)(({ theme }) => ({
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  justifyContent: "space-between",
  transition: "opacity .25s ease-in-out",
  ...(themeConfig.menuTextTruncate && { overflow: "hidden" }),
}));

const VerticalNavLink = ({
  item,
  parent,
  navHover,
  settings,
  navVisible,
  isSubToSub,
  collapsedNavWidth,
  toggleNavVisibility,
  navigationBorderWidth,
}) => {
  // ** Hooks
  const location = useLocation();
  const navigate = useNavigate();
  // ** Vars
  const { navCollapsed } = settings;
  const icon = parent && !item.icon ? themeConfig.navSubItemIcon : item.icon;

  const isNavLinkActive = () => {
    if (
      location.pathname === item.path ||
      handleURLQueries(location, item.path)
    ) {
      return true;
    } else {
      return false;
    }
  };

  const handleClickedItem = (item) => {
    navigate(item.path)
  }

  return (
    // <CanViewNavLink navLink={item}>
    <ListItem
      disablePadding
      className="nav-link"
      disabled={item.disabled || false}
      sx={{ mt: 1, px: "0 !important" }}
      onClick={() => handleClickedItem(item)}
    >
      <MenuNavLink
        component={Link}
        {...(item.disabled && { tabIndex: -1 })}
        className={isNavLinkActive() ? "active" : ""}
        href={item.path === undefined ? "/" : `${item.path}`}
        {...(item.openInNewTab ? { target: "_blank" } : null)}
        onClick={(e) => {
          if (item.path === undefined) {
            e.preventDefault();
            e.stopPropagation();
          }
          if (navVisible) {
            toggleNavVisibility();
          }
        }}
        sx={{
          py: 2,
          ...(item.disabled
            ? { pointerEvents: "none" }
            : { cursor: "pointer" }),
          px:
            navCollapsed && !navHover
              ? 2
              : 2,
          "& .MuiTypography-root, & svg": {
            color: "text.secondary",
          },
        }}
      >
        <ListItemIcon
          sx={{
            transition: "margin .25s ease-in-out",
            ...(navCollapsed && !navHover ? { mr: 0 } : { mr: 2 }),
            ...(parent ? { ml: 1.5, mr: 3.5 } : {}),
            "& svg": {
              fontSize: "0.625rem",
              ...(!parent ? { fontSize: "1.375rem" } : {}),
              ...(parent && item.icon ? { fontSize: "0.875rem" } : {}),
            },
          }}
        >
          {!item.icon ?
            <Badge
              overlap="rectangular"
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
            >
              <CustomAvatar
                skin="light"
                sx={{
                  // width: 24,
                  height: 26,
                  fontSize: (theme) => theme.typography.body1.fontSize,
                  backgroundColor: isNavLinkActive ? "transparent" : "white",
                  // border:"2px solid #726f7b",
                  color: isNavLinkActive() ? "white" : "#726f7b",
                  borderRadius: "3px",
                  // fontSize: "17px"
                }}
              >
                {item.title.charAt(0).toUpperCase()}
              </CustomAvatar>
            </Badge>
            :

            <UserIcon icon={icon} />
          }
        </ListItemIcon>

        <MenuItemTextMetaWrapper
          sx={{
            ...(isSubToSub ? { ml: 2 } : {}),
            ...(navCollapsed && !navHover ? { opacity: 0 } : { opacity: 1 }),
          }}
        >
          <Typography variant="body1"
            {...((themeConfig.menuTextTruncate ||
              (!themeConfig.menuTextTruncate && navCollapsed && !navHover)) && {
              noWrap: true,
            })}
          >
            <Translations text={item.title} isStyleInUse={true} />
          </Typography>
          {item.badgeContent ? (
            <Chip
              size="small"
              label={item.badgeContent}
              color={
                isNavLinkActive() ? item.badgeColor || "primary" : undefined
              }
              sx={{
                height: 22,
                minWidth: 22,
                "& .MuiChip-label": { px: 1.5, textTransform: "capitalize" },
              }}
            />
          ) : null}
        </MenuItemTextMetaWrapper>
      </MenuNavLink>
    </ListItem>
    // </CanViewNavLink>
  );
};

export default VerticalNavLink;
